import React, {memo} from "react"
import {formatTextTestIds} from "@next/utils"
import {type TextModel} from "models/Text"
import {
    FilterContainer,
    FilterFlexContainer,
    FiltersContainer,
    FixedInfoContainer,
    TotalProductsResult,
    TotalProductsTitle,
    ClearAllLink,
} from "./components"
import {useShowFilters} from "../../useShowFilters"
import Filter from "../../../filter"
import KeyFilter from "../../../keyFilter"
import {type Filters} from "../../../../models/Filter"
import {useHideFixedItems} from "../../../../hooks/useHideFixedItems"
import OpenFilterButton from "../../../openFilterButton"
import useSetFiltersOpenFromCookie from "../../../../hooks/useSetFiltersOpenFromCookie"
import {InputContextProvider} from "../../../../hooks/useInputContext"

interface StandardFiltersComponentProps {
    totalResults: number
    consolidatedFilters: string[]
    filters: Filters
    isFilterSelected?: boolean
    clearAllFilters: () => void
    text: TextModel
    initialScrollCompleted: boolean
    url: string
    rememberFilters: boolean
}

export const StandardFiltersComponent = memo(
    ({
        totalResults,
        consolidatedFilters,
        filters,
        isFilterSelected,
        clearAllFilters,
        text,
        initialScrollCompleted,
        url,
        rememberFilters,
    }: StandardFiltersComponentProps) => {
        useSetFiltersOpenFromCookie(url, rememberFilters)
        // let currFilterDisplayName = ""
        const {hideFixedItems} = useHideFixedItems()

        const {
            infoContainerTop,
            infoContainerRef,
            containerRef,
            facetAbsoluteTop,
            showOpenFilterBtn,
            showFixedFacets,
            facetsContainerRef,
            underlayElementHeight,
            totalProductsHeight,
            totalProductsRef,
            handleOpenFilterBtnClick,
            filterContainerHeight,
        } = useShowFilters(hideFixedItems, initialScrollCompleted)

        return (
            <FilterFlexContainer data-testid="plp-filter-standard-template">
                <FilterContainer ref={containerRef} data-testid={formatTextTestIds(`plp-facets`)}>
                    <FixedInfoContainer ref={infoContainerRef} top={infoContainerTop}>
                        <TotalProductsResult ref={totalProductsRef}>
                            <TotalProductsTitle
                                variant="h3"
                                component="p"
                                data-testid="plp-total-products"
                                role="status"
                            >
                                {totalResults} {text.labels.products}
                            </TotalProductsTitle>
                            {isFilterSelected && (
                                <ClearAllLink
                                    data-testid="plp-filter-clear-all-link"
                                    onClick={clearAllFilters}
                                    aria-label={text.labels.clearText}
                                >
                                    {text.labels.clearText}
                                </ClearAllLink>
                            )}
                        </TotalProductsResult>
                        {showOpenFilterBtn && (
                            <OpenFilterButton
                                handleOpenFilterBtnClick={handleOpenFilterBtnClick}
                                top={infoContainerTop}
                                $height={filterContainerHeight}
                            />
                        )}
                    </FixedInfoContainer>
                    <FiltersContainer
                        ref={facetsContainerRef}
                        absoluteTop={facetAbsoluteTop}
                        showFixedFacets={showFixedFacets}
                        hide={hideFixedItems}
                        data-testid="plp-facet-items"
                        isFiltersHidden={showOpenFilterBtn}
                        totalProductsHeight={totalProductsHeight}
                    >
                        <InputContextProvider>
                            {consolidatedFilters.map(filter => {
                                let currFilterDisplayName
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                Object.entries(filters).forEach(([key, value]) => {
                                    if (value.name === filter) currFilterDisplayName = value.displayName
                                })
                                switch (filters[filter].type) {
                                    case "feat":
                                        return <KeyFilter key={filter} name={filter} />
                                    default:
                                        return <Filter key={filter} name={filter} displayName={currFilterDisplayName} />
                                }
                            })}
                        </InputContextProvider>
                    </FiltersContainer>
                    {showFixedFacets && <div style={{height: underlayElementHeight}} />}
                </FilterContainer>
            </FilterFlexContainer>
        )
    },
)
StandardFiltersComponent.displayName = "StandardFiltersComponent"

export default StandardFiltersComponent
